import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PropTypes from "prop-types";
import EquipmentMasterCatalogApi from "../../../../../infrastructure/api/equipmentMasterCatalog";
import NewEquipmentItemRequestDialog from "./NewEquipmentItemRequestDialog";

/**
 * استخراج بازه/مقدار توان از مدل یا عنوان (مثلاً 560-575 → «560–575 وات»).
 */
const extractWattLabel = (o) => {
  if (!o) return "";
  const source = [o.modelName, o.shortTitle, o.nameFa].filter(Boolean).join(" ");
  if (!source) return "";
  if (/وات|\bW\b|\bkW\b|\bKW\b/i.test(source) && !/\d/.test(source)) return "";

  const range = source.match(/\b(\d{3,4})\s*[-–—]\s*(\d{3,4})\b/);
  if (range) {
    const a = Number(range[1]);
    const b = Number(range[2]);
    if (a >= 50 && b >= 50 && b >= a && b <= 2000) return `${a}–${b} W`;
  }

  const singleW = source.match(/\b(\d{2,4})\s*[Ww]\b/);
  if (singleW) {
    const w = Number(singleW[1]);
    if (w >= 50 && w <= 2000) return `${w} W`;
  }

  const kw = source.match(/\b(\d+(?:[.,]\d+)?)\s*[kK][Ww]\b/);
  if (kw) return `${kw[1].replace(",", ".")} kW`;

  // مدل‌های رایج پنل بدون پسوند W: ...550-570...
  const embedded = source.match(/(?:^|[^0-9])(\d{3})\s*[-–—]\s*(\d{3})(?:[^0-9]|$)/);
  if (embedded) {
    const a = Number(embedded[1]);
    const b = Number(embedded[2]);
    if (a >= 200 && b >= 200 && b >= a && b <= 900) return `${a}–${b} W`;
  }

  return "";
};

/**
 * عنوان اصلی کاربرپسند: برند + نام فارسی/عنوان کوتاه (بدون PublicCode).
 */
const getPrimaryTitle = (o) => {
  if (!o) return "";
  const name = (o.nameFa || o.shortTitle || "").trim();
  const brand = (o.brandName || "").trim();
  if (brand && name) {
    if (name.includes(brand)) return name;
    return `${brand} — ${name}`;
  }
  return name || brand || o.modelName || "";
};

/**
 * خط دوم: مدل و دسته (وات جداگانه در خط اصلی می‌آید).
 */
const getSecondaryMeta = (o) => {
  if (!o) return "";
  const primary = getPrimaryTitle(o);
  const parts = [];
  const model = (o.modelName || "").trim();
  if (model && model !== o.shortTitle && model !== o.nameFa && !primary.includes(model)) {
    parts.push(model);
  }
  if (o.categoryTitle) parts.push(o.categoryTitle);
  return parts.filter(Boolean).join(" • ");
};

/**
 * لیبل داخل فیلد انتخاب‌شده: نام/برند/مدل + وات + واحد — بدون کد استاندارد.
 */
const getOptionLabel = (o) => {
  if (!o) return "";
  const title = getPrimaryTitle(o);
  const model = (o.modelName || "").trim();
  let label = title || model || "";
  if (model && title && !title.includes(model)) label = `${title} | ${model}`;
  const watt = extractWattLabel(o);
  if (watt && label && !/\d+\s*[–-]?\s*\d*\s*W\b/i.test(label) && !/\bkW\b/i.test(label)) {
    label = `${label} · ${watt}`;
  }
  const unit = (o.defaultUnit || "").trim();
  if (unit && label && !label.includes(unit)) label = `${label} · ${unit}`;
  else if (unit && !label) label = unit;
  return label;
};

/**
 * انتخابگر تجهیز از کاتالوگ مرجع (فقط تجهیزات تأییدشده).
 * خروجی onChange یک شیء ایمن است: { equipmentItemId, publicCode, shortTitle, brandName, modelName, defaultUnit }
 */
const EquipmentItemPicker = ({
  value = null,
  onChange,
  label = "انتخاب تجهیز از کاتالوگ مرجع",
  categoryId = null,
  equipmentTypeEnum = null,
  disabled = false,
  required = false,
  error = false,
  helperText = "",
  allowRequestNew = false,
  submitNewRequest = null,
  requestNewLabel = "تجهیز در لیست نیست؟ درخواست افزودن به کاتالوگ",
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState("");
  const [requestOpen, setRequestOpen] = useState(false);
  const debounceRef = useRef(null);
  const resolvedType =
    equipmentTypeEnum === "" || equipmentTypeEnum == null
      ? null
      : Number(equipmentTypeEnum);

  const normalizeSearchTerm = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    const composed =
      s.includes("—") || s.includes("–") || s.includes("|") || /\(.*\)/.test(s);
    const codeMatch = s.match(/\b([A-Za-z]{2,5}-\d+)\b/);
    if (composed && codeMatch) return codeMatch[1];
    return s;
  };

  const fetchOptions = async (searchTerm) => {
    setLoading(true);
    try {
      const res = await EquipmentMasterCatalogApi.search({
        term: normalizeSearchTerm(searchTerm),
        categoryId: categoryId || null,
        equipmentTypeEnum:
          resolvedType && !Number.isNaN(resolvedType) ? resolvedType : null,
        onlyApproved: true,
        pageNumber: 1,
        // Newly approved items can sit past the first page when sorted by PublicCode.
        pageSize: 200,
      });
      const items = res?.data?.items ?? res?.data?.Items ?? [];
      setOptions(items);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOptions([]);
  }, [categoryId, resolvedType]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(term), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, open, categoryId, resolvedType]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    if (typeof value === "object") return value;
    return options.find((o) => o.equipmentItemId === value) || null;
  }, [value, options]);

  const canRequestNew =
    allowRequestNew && typeof submitNewRequest === "function" && !disabled;

  const selectedCodeHint = selectedOption?.publicCode
    ? `کد استاندارد: ${selectedOption.publicCode}`
    : "";
  const mergedHelper = [helperText, selectedCodeHint].filter(Boolean).join(" — ");

  return (
    <Box>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disabled={disabled}
        options={options}
        value={selectedOption}
        loading={loading}
        isOptionEqualToValue={(o, v) => o.equipmentItemId === v?.equipmentItemId}
        getOptionLabel={getOptionLabel}
        filterOptions={(x) => x}
        onInputChange={(e, v, reason) => {
          if (reason === "reset") return;
          setTerm(v);
        }}
        onChange={(e, v) => {
          if (!onChange) return;
          onChange(
            v
              ? {
                  equipmentItemId: v.equipmentItemId,
                  publicCode: v.publicCode,
                  shortTitle: v.shortTitle,
                  nameFa: v.nameFa,
                  brandName: v.brandName,
                  modelName: v.modelName,
                  defaultUnit: v.defaultUnit,
                  categoryTitle: v.categoryTitle,
                }
              : null
          );
        }}
        renderOption={(props, o) => {
          const primary = getPrimaryTitle(o);
          const watt = extractWattLabel(o);
          const unit = (o.defaultUnit || "").trim();
          const bits = [primary || o.modelName || ""].filter(Boolean);
          if (watt && !bits.some((b) => /\d+\s*[–-]?\s*\d*\s*W\b/i.test(b) || /\bkW\b/i.test(b)))
            bits.push(watt);
          if (unit && !bits.some((b) => b.includes(unit))) bits.push(unit);
          const primaryWithUnit = bits.length ? bits.join(" · ") : "—";
          const secondary = getSecondaryMeta(o);
          return (
            <Box component="li" {...props} key={o.equipmentItemId}>
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                  {primaryWithUnit}
                </Typography>
                {secondary ? (
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {secondary}
                  </Typography>
                ) : null}
                {o.publicCode ? (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{
                      mt: 0.25,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      letterSpacing: 0.4,
                      direction: "ltr",
                      textAlign: "left",
                      alignSelf: "flex-start",
                    }}
                  >
                    {o.publicCode}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            helperText={mergedHelper || undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {canRequestNew ? (
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{ mt: 0.75 }}
          onClick={() => setRequestOpen(true)}
        >
          {requestNewLabel}
        </Button>
      ) : null}

      {canRequestNew ? (
        <NewEquipmentItemRequestDialog
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          submitRequest={submitNewRequest}
        />
      ) : null}
    </Box>
  );
};

EquipmentItemPicker.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  label: PropTypes.string,
  categoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  equipmentTypeEnum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  allowRequestNew: PropTypes.bool,
  submitNewRequest: PropTypes.func,
  requestNewLabel: PropTypes.string,
};

export default EquipmentItemPicker;
